/**
 * BRAND SERVICE
 * 
 * Handles all brand-related operations including:
 * - Brand lookup and queries
 * - Brand claim submission and verification
 * - Admin functions for approving/denying claims
 * - Brand rating system
 */

import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  increment,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import type { Brand, BrandClaimRequest, VerificationMethod } from '../types/brand';
import { polishSlug } from '../utils/polishSlug';

// ============================================
// BRAND LOOKUP & QUERIES
// ============================================

/**
 * Get brand by slug
 */
export async function getBrandBySlug(slug: string): Promise<Brand | null> {
  const q = query(collection(db, 'brands'), where('slug', '==', slug));
  const snapshot = await getDocs(q);
  
  if (snapshot.empty) return null;
  
  const doc = snapshot.docs[0];
  return { id: doc.id, ...doc.data() } as Brand;
}

/**
 * Get brands owned by a user
 * Checks both ownerId and ownerEmail for ownership
 */
export async function getUserOwnedBrands(userId: string, userEmail?: string): Promise<Brand[]> {
  const ownedBrands: Brand[] = [];
  
  // Query by ownerId
  const byIdQuery = query(collection(db, 'brands'), where('ownerId', '==', userId));
  const byIdSnapshot = await getDocs(byIdQuery);
  byIdSnapshot.docs.forEach(doc => {
    ownedBrands.push({ id: doc.id, ...doc.data() } as Brand);
  });
  
  // Also query by ownerEmail if provided
  if (userEmail) {
    const byEmailQuery = query(collection(db, 'brands'), where('ownerEmail', '==', userEmail.toLowerCase()));
    const byEmailSnapshot = await getDocs(byEmailQuery);
    byEmailSnapshot.docs.forEach(doc => {
      // Avoid duplicates
      if (!ownedBrands.find(b => b.id === doc.id)) {
        ownedBrands.push({ id: doc.id, ...doc.data() } as Brand);
      }
    });
  }
  
  return ownedBrands;
}

// ============================================
// BRAND CLAIM SUBMISSION
// ============================================

/**
 * Submit a brand claim request
 */
export async function submitBrandClaim(
  brandId: string,
  userId: string,
  userEmail: string,
  userName: string,
  method: VerificationMethod,
  details: {
    email?: string | null;
    socialHandle?: string | null;
    verificationCode?: string | null;
    notes?: string | null;
    role?: string | null;
  }
): Promise<void> {
  const claimRequest: BrandClaimRequest = {
    userId,
    userEmail,
    userName,
    method,
    submittedAt: new Date(),
    domainEmail: details.email || null,
    socialHandle: details.socialHandle || null,
    verificationCode: details.verificationCode || null,
    notes: details.notes || null,
    role: details.role || null,
  };

  await updateDoc(doc(db, 'brands', brandId), {
    status: 'claimed',
    claimRequest,
    updatedAt: serverTimestamp(),
  });
}

// ============================================
// BRAND CLAIM & VERIFICATION (ADMIN)
// ============================================

/**
 * Get all pending brand claims
 */
export async function getPendingBrandClaims(): Promise<Brand[]> {
  const q = query(
    collection(db, 'brands'),
    where('status', '==', 'claimed'),
    orderBy('claimRequest.submittedAt', 'desc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as Brand[];
}

/**
 * Get recently processed claims (last 30 days)
 */
export async function getRecentlyProcessedClaims(): Promise<Brand[]> {
  const q = query(
    collection(db, 'brands'),
    where('status', 'in', ['verified', 'rejected']),
    orderBy('verifiedAt', 'desc')
  );
  
  const snapshot = await getDocs(q);
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  
  return snapshot.docs
    .map(doc => ({ id: doc.id, ...doc.data() }) as Brand)
    .filter(brand => {
      if (!brand.verifiedAt) return false;
      const verifiedDate = brand.verifiedAt instanceof Timestamp
        ? brand.verifiedAt.toDate()
        : new Date(brand.verifiedAt);
      return verifiedDate > thirtyDaysAgo;
    });
}

/**
 * Approve a brand claim
 * - Updates brand status to 'verified'
 * - Sets owner info on brand
 * - Updates user role to 'brand'
 */
export async function approveBrandClaim(
  brandId: string,
  adminUserId: string
): Promise<void> {
  // Get the brand to access claim info
  const brandDoc = await getDoc(doc(db, 'brands', brandId));
  if (!brandDoc.exists()) {
    throw new Error('Brand not found');
  }
  
  const brand = brandDoc.data() as Brand;
  if (!brand.claimRequest) {
    throw new Error('No claim request found');
  }
  
  const { userId, userEmail, userName } = brand.claimRequest;
  
  // Use the verified domain email if available, otherwise fall back to user's email
  const verifiedEmail = brand.claimRequest.domainEmail || userEmail;
  
  // Update brand document
  await updateDoc(doc(db, 'brands', brandId), {
    status: 'verified',
    ownerId: userId,
    ownerEmail: verifiedEmail, // Use the verified email for ownership checks
    ownerName: userName,
    verifiedAt: serverTimestamp(),
    verifiedBy: adminUserId,
    updatedAt: serverTimestamp(),
    // Keep claimRequest for audit trail
  });
  
  // Update user document with brand ownership
  await updateDoc(doc(db, 'users', userId), {
    role: 'brand',
    ownsBrandId: brandId,
    ownsBrandName: brand.name,
  });
}

/**
 * Deny a brand claim
 * - Resets brand to 'unclaimed' 
 * - Clears claim request
 * - Records denial reason
 */
export async function denyBrandClaim(
  brandId: string,
  adminUserId: string,
  reason?: string
): Promise<void> {
  await updateDoc(doc(db, 'brands', brandId), {
    status: 'unclaimed',
    claimRequest: null,
    lastDenialReason: reason || 'No reason provided',
    lastDeniedAt: serverTimestamp(),
    lastDeniedBy: adminUserId,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Revoke brand ownership (if needed later)
 */
export async function revokeBrandOwnership(
  brandId: string,
  adminUserId: string,
  reason?: string
): Promise<void> {
  // Get brand to find current owner
  const brandDoc = await getDoc(doc(db, 'brands', brandId));
  if (!brandDoc.exists()) {
    throw new Error('Brand not found');
  }
  
  const brand = brandDoc.data() as Brand;
  const previousOwnerId = brand.ownerId;
  
  // Update brand - back to unclaimed
  await updateDoc(doc(db, 'brands', brandId), {
    status: 'unclaimed',
    ownerId: null,
    ownerEmail: null,
    ownerName: null,
    revokedAt: serverTimestamp(),
    revokedBy: adminUserId,
    revokeReason: reason,
    updatedAt: serverTimestamp(),
  });
  
  // Update previous owner's user doc
  if (previousOwnerId) {
    await updateDoc(doc(db, 'users', previousOwnerId), {
      role: 'collector',
      ownsBrandId: null,
      ownsBrandName: null,
    });
  }
}

/**
 * Get all verified brands with their owners
 */
export async function getVerifiedBrands(): Promise<Brand[]> {
  const q = query(
    collection(db, 'brands'),
    where('status', '==', 'verified'),
    orderBy('name', 'asc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as Brand[];
}

// ============================================
// BRAND RATING FUNCTIONS
// ============================================

/**
 * Rate a brand with multiple category ratings
 * - Creates/updates user's rating for the brand
 * - Ratings is an object with category keys and values 1-5
 */
export async function rateBrand(
  brandId: string,
  userId: string,
  ratings: Record<string, number>,
  review?: string
): Promise<void> {
  // Validate all ratings are between 1 and 5
  for (const [category, rating] of Object.entries(ratings)) {
    if (rating < 1 || rating > 5) {
      throw new Error(`Rating for ${category} must be between 1 and 5`);
    }
  }

  const ratingRef = doc(db, 'brands', brandId, 'ratings', userId);

  // Check if user already rated this brand
  const existingRating = await getDoc(ratingRef);

  // Save the user's rating
  await setDoc(ratingRef, {
    userId,
    ...ratings,
    review: review || null,
    createdAt: existingRating.exists() ? existingRating.data().createdAt : serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Get a user's rating for a brand
 */
export async function getUserBrandRating(
  brandId: string,
  userId: string
): Promise<{ rating: number; review?: string } | null> {
  const ratingRef = doc(db, 'brands', brandId, 'ratings', userId);
  const ratingDoc = await getDoc(ratingRef);
  
  if (!ratingDoc.exists()) return null;
  
  const data = ratingDoc.data();
  return {
    rating: data.rating,
    review: data.review || undefined,
  };
}

/**
 * Delete a user's rating for a brand
 */
export async function deleteBrandRating(
  brandId: string,
  userId: string
): Promise<void> {
  const ratingRef = doc(db, 'brands', brandId, 'ratings', userId);
  const brandRef = doc(db, 'brands', brandId);
  
  // Get the existing rating first
  const existingRating = await getDoc(ratingRef);
  if (!existingRating.exists()) return;
  
  const previousRating = existingRating.data().rating;
  
  // Delete the rating document
  await deleteDoc(ratingRef);
  
  // Update brand's rating stats
  await updateDoc(brandRef, {
    ratingCount: increment(-1),
    ratingTotal: increment(-previousRating),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Get all ratings for a brand
 */
export async function getBrandRatings(brandId: string): Promise<Array<{
  userId: string;
  rating: number;
  review?: string;
  createdAt: Date;
}>> {
  const ratingsRef = collection(db, 'brands', brandId, 'ratings');
  const snapshot = await getDocs(ratingsRef);
  
  return snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      userId: data.userId,
      rating: data.rating,
      review: data.review || undefined,
      createdAt: data.createdAt instanceof Timestamp 
        ? data.createdAt.toDate() 
        : new Date(data.createdAt),
    };
  });
}

// ============================================
// BRAND FOLLOW FUNCTIONS
// ============================================

/**
 * Follow a brand
 * - Adds brand to user's followed brands
 * - Increments brand's follower count
 */
export async function followBrand(
  brandId: string,
  userId: string
): Promise<void> {
  const followRef = doc(db, 'brands', brandId, 'followers', userId);
  const brandRef = doc(db, 'brands', brandId);
  const userFollowRef = doc(db, 'users', userId, 'followedBrands', brandId);
  
  // Check if already following
  const existingFollow = await getDoc(followRef);
  if (existingFollow.exists()) {
    return; // Already following
  }
  
  // Add to brand's followers subcollection
  await setDoc(followRef, {
    userId,
    followedAt: serverTimestamp(),
  });
  
  // Add to user's followed brands subcollection
  await setDoc(userFollowRef, {
    brandId,
    followedAt: serverTimestamp(),
  });
  
  // Increment brand's follower count
  await updateDoc(brandRef, {
    followerCount: increment(1),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Unfollow a brand
 * - Removes brand from user's followed brands
 * - Decrements brand's follower count
 */
export async function unfollowBrand(
  brandId: string,
  userId: string
): Promise<void> {
  const followRef = doc(db, 'brands', brandId, 'followers', userId);
  const brandRef = doc(db, 'brands', brandId);
  const userFollowRef = doc(db, 'users', userId, 'followedBrands', brandId);
  
  // Check if actually following
  const existingFollow = await getDoc(followRef);
  if (!existingFollow.exists()) {
    return; // Not following
  }
  
  // Remove from brand's followers subcollection
  await deleteDoc(followRef);
  
  // Remove from user's followed brands subcollection
  await deleteDoc(userFollowRef);
  
  // Decrement brand's follower count
  await updateDoc(brandRef, {
    followerCount: increment(-1),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Check if user is following a brand
 */
export async function isFollowingBrand(
  brandId: string,
  userId: string
): Promise<boolean> {
  const followRef = doc(db, 'brands', brandId, 'followers', userId);
  const followDoc = await getDoc(followRef);
  return followDoc.exists();
}

/**
 * Get all brands a user is following
 */
export async function getUserFollowedBrands(userId: string): Promise<string[]> {
  const followedRef = collection(db, 'users', userId, 'followedBrands');
  const snapshot = await getDocs(followedRef);
  return snapshot.docs.map(doc => doc.id);
}

/**
 * Get all followers of a brand
 */
export async function getBrandFollowers(brandId: string): Promise<string[]> {
  const followersRef = collection(db, 'brands', brandId, 'followers');
  const snapshot = await getDocs(followersRef);
  return snapshot.docs.map(doc => doc.id);
}

// ============================================
// BRAND DATA AGGREGATION
// ============================================

/**
 * Get aggregated collections for a brand
 * Finds all unique collection names from items with this brand
 * Includes the items in each collection
 * 
 * Events (PPU, HHC, etc.) are now separated by year/month/theme
 */
export async function getAggregatedCollections(brandName: string): Promise<Array<{
  name: string;
  count: number;
  year?: number;
  season?: string;
  itemCount: number;
  releaseType?: string;
  isEvent?: boolean;
  isCore?: boolean;
  theme?: string;
  eventSource?: string;
  items: Array<{
    id: string;
    name: string;
    itemType?: string;
    nameSlug?: string;
    colorDescription?: string;
    photos?: Array<{ url: string }>;
    colorFamily?: string[];
    finish?: string[];
    releaseYear?: number;
    releaseSeason?: string;
  }>;
}>> {
  const itemsRef = collectionGroup(db, 'items');
  // Query by brandLower so items match regardless of how the user typed
  // the brand — "OPI", "opi", " OPI " all resolve to the same bucket.
  // The old exact-case match on `brand` silently hid items whose casing
  // didn't match the brand doc's display name.
  const q = query(itemsRef, where('brandLower', '==', brandName.toLowerCase()));
  const snapshot = await getDocs(q);

  // Aggregate collections with their items.
  //
  // `items` is a Map keyed on the polish's canonical identity — a
  // slug like `mooncat__willow-in-the-wind` — so the same polish
  // owned by N collectors collapses to ONE tile instead of N repeated
  // tiles. Cross-user browse views should dedupe by polish; the
  // per-polish detail page still shows every collector's swatches.
  // See services/polishRename.ts for how the identity fields are kept
  // consistent across items.
  const collectionsMap = new Map<string, {
    count: number;
    year?: number;
    season?: string;
    releaseType?: string;
    isEvent?: boolean;
    isCore?: boolean;
    theme?: string;
    displayName?: string;
    eventSource?: string;
    items: Map<string, {
      id: string;
      name: string;
      itemType?: string;
      nameSlug?: string;
      colorDescription?: string;
      photos?: Array<{ url: string }>;
      colorFamily?: string[];
      finish?: string[];
      releaseYear?: number;
      releaseSeason?: string;
    }>;
  }>();
  
  snapshot.docs.forEach(docSnap => {
    const data = docSnap.data();
    // Skip soft-deleted items and items hidden by a ban cascade. The
    // `hiddenByBan` flag is written by the onModerationStateChange trigger
    // when the owning user is banned so their contributions disappear from
    // brand pages without touching independently user-deleted items. Also
    // skip items the owner marked private — they should only surface on
    // their own collection page, never in cross-user brand aggregations.
    if (data.isDeleted === true || data.hiddenByBan === true || data.isPrivate === true) return;
    let collectionName = data.collectionName || 'Uncategorized';
    let displayName = collectionName;

    // Handle events specially - create unique keys for each event instance
    // Events like PPU, HHC, Charity should be separated by month/year/theme
    if (data.eventSource || data.releaseType === 'event') {
      const eventName = data.eventSource || data.collectionName || 'Event';
      const year = data.releaseYear || '';
      const month = data.releaseMonth || data.releaseSeason || '';
      const theme = data.eventTheme || '';
      
      // Create a unique key for this specific event instance
      // e.g., "PPU|2024|January|Cozy Nights" or "HHC|2024|March|"
      const keyParts = [eventName, year, month, theme].filter(Boolean);
      collectionName = keyParts.join('|');
      
      // Create a nice display name
      // e.g., "PPU January 2024 - Cozy Nights" or "HHC March 2024"
      if (theme && month && year) {
        displayName = `${eventName} ${month} ${year} - ${theme}`;
      } else if (month && year) {
        displayName = `${eventName} ${month} ${year}`;
      } else if (theme && year) {
        displayName = `${eventName} ${year} - ${theme}`;
      } else if (year) {
        displayName = `${eventName} ${year}`;
      } else {
        displayName = eventName;
      }
    }
    
    // Handle core/permanent items
    if (data.releaseType === 'core' || (!data.collectionName && !data.eventSource && !data.releaseYear)) {
      collectionName = 'Core / Permanent';
      displayName = 'Core / Permanent';
    }
    
    const itemData = {
      id: docSnap.id,
      name: data.name || 'Unknown',
      // Preserve itemType so the brand-page tile knows which detail
      // route to link to. Default to 'polish' for legacy items that
      // predate universal itemType stamping — the app was polish-only
      // for a long time so this matches historical intent.
      itemType: (data.itemType as string | undefined) || 'polish',
      // Preserve nameSlug for URL construction on the tile. Missing on
      // legacy items — callers slugify(name) as a fallback there.
      nameSlug: (data.nameSlug as string | undefined) || undefined,
      colorDescription: data.colorDescription,
      photos: data.photos || [],
      colorFamily: data.colorFamily || [],
      finish: data.finish || [],
      // Also project baseType/holoType/specialFeatures so brand-page
      // polish cards can show the same structured chip strips as the
      // detail page. Not part of the earlier projection because tile
      // display only rendered finish; new BrandItemCard scope shows
      // all four.
      baseType: (data.baseType as string | undefined) || undefined,
      holoType: data.holoType || [],
      specialFeatures: data.specialFeatures || [],
      releaseYear: data.releaseYear,
      releaseSeason: data.releaseSeason,
    };
    
    // Canonical identity for dedupe. Prefer the item's own `slug`
    // (Phase-1 stamped every polish item with `brandSlug__nameSlug`); fall
    // back to a computed slug for legacy items missing it. Two different
    // collectors owning the same polish resolve to the same key here, so
    // their user-items merge into one tile.
    const itemIdentity: string =
      (typeof data.slug === 'string' && data.slug) ||
      polishSlug(
        typeof data.brand === 'string' ? data.brand : brandName,
        typeof data.name === 'string' ? data.name : ''
      );

    const existing = collectionsMap.get(collectionName);
    if (existing) {
      const existingItem = existing.items.get(itemIdentity);
      if (existingItem) {
        // Merge — keep the first-seen entry but fill in any field that's
        // currently empty from this new item. Matches the mobile brand
        // page's merge pattern in polish-all-the-things-app's
        // getAggregatedReleases. Predictable and stable — subsequent
        // renders don't jump between users' photos.
        if (!existingItem.colorDescription && itemData.colorDescription) {
          existingItem.colorDescription = itemData.colorDescription;
        }
        if (
          (!existingItem.photos || existingItem.photos.length === 0) &&
          itemData.photos && itemData.photos.length > 0
        ) {
          existingItem.photos = itemData.photos;
        }
        if (
          (!existingItem.colorFamily || existingItem.colorFamily.length === 0) &&
          itemData.colorFamily && itemData.colorFamily.length > 0
        ) {
          existingItem.colorFamily = itemData.colorFamily;
        }
        if (
          (!existingItem.finish || existingItem.finish.length === 0) &&
          itemData.finish && itemData.finish.length > 0
        ) {
          existingItem.finish = itemData.finish;
        }
        if (!existingItem.releaseYear && itemData.releaseYear) {
          existingItem.releaseYear = itemData.releaseYear;
          existingItem.releaseSeason = itemData.releaseSeason;
        }
      } else {
        existing.count++;
        existing.items.set(itemIdentity, itemData);
      }
      // Keep the most recent year/season if available
      if (data.releaseYear && (!existing.year || data.releaseYear > existing.year)) {
        existing.year = data.releaseYear;
        existing.season = data.releaseSeason;
      }
      // Update theme if not set
      if (data.eventTheme && !existing.theme) {
        existing.theme = data.eventTheme;
      }
    } else {
      const itemsMap = new Map<string, typeof itemData>();
      itemsMap.set(itemIdentity, itemData);
      collectionsMap.set(collectionName, {
        count: 1,
        year: data.releaseYear,
        season: data.releaseSeason,
        releaseType: data.releaseType,
        isEvent: data.releaseType === 'event' || !!data.eventSource,
        isCore: data.releaseType === 'core' || collectionName === 'Core / Permanent',
        theme: data.eventTheme,
        displayName: displayName,
        eventSource: data.eventSource,
        items: itemsMap,
      });
    }
  });
  
  // Convert to array and sort by count (distinct polishes, thanks to
  // the dedupe above). `items` is a Map at this point; flatten to array
  // for the caller. Sort by name inside each collection so tile order
  // is stable across page loads.
  return Array.from(collectionsMap.entries())
    .map(([key, data]) => ({
      name: data.displayName || key,  // Use displayName for nice formatting
      count: data.count,
      itemCount: data.count,
      year: data.year,
      season: data.season,
      releaseType: data.releaseType,
      isEvent: data.isEvent,
      isCore: data.isCore,
      theme: data.theme,
      eventSource: data.eventSource,
      items: Array.from(data.items.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
    }))
    .sort((a, b) => {
      // Core first, then by year descending, then by name
      if (a.isCore && !b.isCore) return -1;
      if (!a.isCore && b.isCore) return 1;
      if (a.year && b.year) return b.year - a.year;
      if (a.year && !b.year) return -1;
      if (!a.year && b.year) return 1;
      return a.name.localeCompare(b.name);
    });
}

/**
 * Get brand collections from the brand document
 * (Alternative to aggregated - uses stored collection data)
 */
export async function getBrandCollections(brandId: string): Promise<Array<{
  id: string;
  name: string;
  description?: string;
  coverImage?: string;
  year?: number;
  releaseYear?: number;
  season?: string;
  releaseSeason?: string;
  releaseDate?: string;
  itemCount: number;
  releaseType?: string;
  isEvent?: boolean;
  isCore?: boolean;
  theme?: string;
  collabWith?: string;
  collabTheme?: string;
  eventName?: string;
  eventTheme?: string;
  items?: Array<{
    id: string;
    name: string;
    colorDescription?: string;
    photos?: Array<{ url: string }>;
    colorFamily?: string[];
    finish?: string[];
    releaseYear?: number;
    releaseSeason?: string;
  }>;
}>> {
  // Get brand document first to check status
  const brandDoc = await getDoc(doc(db, 'brands', brandId));
  if (!brandDoc.exists()) {
    return [];
  }
  
  const brandData = brandDoc.data();
  
  // For verified brands, read from the official collections subcollection
  if (brandData.status === 'verified') {
    const collectionsRef = collection(db, 'brands', brandId, 'collections');
    const collectionsQuery = query(collectionsRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(collectionsQuery);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      itemCount: 0, // Will be calculated by consuming component based on products
    })) as any;
  }
  
  // For community/claimed brands, use aggregated collections from user data
  const brandName = brandData.name;
  const aggregated = await getAggregatedCollections(brandName);
  return aggregated.map((c, index) => ({
    id: `collection-${index}`,
    name: c.name,
    year: c.year,
    season: c.season,
    itemCount: c.count,
    releaseType: c.releaseType,
    isEvent: c.isEvent,
    isCore: c.isCore,
    theme: c.theme,
    items: c.items,
  }));
}

/**
 * Get brand statistics
 * Aggregates data from all items with this brand
 * 
 * - totalCollectors: Unique users who OWN at least one item (not wishlisted)
 * - totalWishlisted: Total COUNT of items on wishlists across all users
 */
export async function getBrandStats(brandName: string): Promise<{
  totalItems: number;
  totalCollectors: number;
  totalWishlisted: number;
  collections: number;
  topColors: Array<{ color: string; count: number }>;
}> {
  const itemsRef = collectionGroup(db, 'items');
  // See getAggregatedCollections above — brandLower catches all case
  // variants so stats reflect every collector's items regardless of typing.
  const q = query(itemsRef, where('brandLower', '==', brandName.toLowerCase()));
  const snapshot = await getDocs(q);

  const collectors = new Set<string>();  // Unique users who OWN items
  const collections = new Set<string>();
  const colorCounts = new Map<string, number>();
  let totalWishlisted = 0;  // Count of wishlist ITEMS
  let totalOwned = 0;
  
  snapshot.docs.forEach(doc => {
    const data = doc.data();
    // Skip items the owner marked private so brand stats reflect only
    // publicly-visible contributions. Deleted / ban-hidden items already
    // don't count toward stats here (they never made it into the query
    // window's semantics for owner/wishlist tallies) but private items
    // do need an explicit filter to keep the counts honest.
    if (data.isPrivate === true) return;

    // Track unique collectors (user IDs from parent path)
    const userId = doc.ref.parent.parent?.id;
    
    // Track unique collections
    if (data.collectionName) collections.add(data.collectionName);
    
    // Count wishlisted items vs owned items
    if (data.onWishlist === true) {
      // Count total wishlist ITEMS (not unique users)
      totalWishlisted++;
    } else {
      totalOwned++;
      // Only count as collector if they OWN items (not just wishlisted)
      if (userId) collectors.add(userId);
    }
    
    // Track color families
    if (data.colorFamily && Array.isArray(data.colorFamily)) {
      data.colorFamily.forEach((color: string) => {
        colorCounts.set(color, (colorCounts.get(color) || 0) + 1);
      });
    }
  });
  
  // Get top 5 colors
  const topColors = Array.from(colorCounts.entries())
    .map(([color, count]) => ({ color, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);
  
  return {
    totalItems: totalOwned,  // Only count owned items, not wishlisted
    totalCollectors: collectors.size,
    totalWishlisted,
    collections: collections.size,
    topColors,
  };
}

// ============================================
// BRAND LISTING & SEARCH
// ============================================

/**
 * Get all brands (with optional filters)
 */
export async function getBrands(options?: {
  status?: 'all' | 'verified' | 'unclaimed' | 'claimed';
  limit?: number;
  orderByField?: string;
  orderDirection?: 'asc' | 'desc';
}): Promise<{ brands: Brand[] }> {
  const { 
    status = 'all', 
    orderByField = 'name', 
    orderDirection = 'asc' 
  } = options || {};
  
  let q;
  
  // Handle nested field names like 'stats.totalCollectors'
  const fieldName = orderByField.includes('.') ? orderByField : orderByField;
  
  if (status === 'all') {
    q = query(
      collection(db, 'brands'),
      orderBy(fieldName, orderDirection)
    );
  } else {
    q = query(
      collection(db, 'brands'),
      where('status', '==', status),
      orderBy(fieldName, orderDirection)
    );
  }
  
  const snapshot = await getDocs(q);
  const brands = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as Brand[];
  
  return { brands };
}

// Rank by admin-curated quality flag so verified canonical brands show up
// above community-created duplicates. Lower number = higher priority.
const CURATION_RANK: Record<string, number> = {
  verified: 0,
  community: 1,
  'needs-review': 2,
};
function curationRank(b: Brand): number {
  return CURATION_RANK[b.curationStatus ?? 'community'] ?? 1;
}

/**
 * Search brands by name
 */
export async function searchBrands(searchTerm: string, limit?: number): Promise<Brand[]> {
  // Firestore doesn't support full-text search, so we fetch all and filter
  // For production, consider Algolia or Elasticsearch
  const { brands: allBrands } = await getBrands();

  const term = searchTerm.toLowerCase().trim();
  let results = allBrands.filter(brand =>
    brand.name.toLowerCase().includes(term) ||
    brand.slug?.toLowerCase().includes(term)
  );

  // Within the matching set, prefer verified brands first (canonical
  // records), then community (auto-created), then needs-review. Ties
  // fall back to existing name alphabetical order — getBrands already
  // returned them name-sorted.
  results.sort((a, b) => curationRank(a) - curationRank(b));

  if (limit) {
    results = results.slice(0, limit);
  }

  return results;
}

/**
 * Get brand by ID
 */
export async function getBrandById(brandId: string): Promise<Brand | null> {
  const brandDoc = await getDoc(doc(db, 'brands', brandId));
  
  if (!brandDoc.exists()) return null;
  
  return { id: brandDoc.id, ...brandDoc.data() } as Brand;
}
